import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Select,
  SelectItem,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
} from '@nextui-org/react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import ChartDownloadButton from '../../../../../common/src/components/charts/ChartDownloadButton';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import ModalSlideFilter from '../../../../../common/src/components/filters/modal-sidebar-filter';
import {
  getProductionVsIndustry,
  ProductionCaliberCurveRow,
  ProductionMarketCurveRow,
  ProductionFormatUnitWeightCurveRow,
  ProductionFreightTypeCurveRow,
  ProductionVarietyCurveRow,
  ProductionVsIndustryResult,
  ProductionWeeklyCurveRow,
} from 'common/src/api/settlements';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import { formatNumber } from '../../../utils/functions';
import { logUserAction } from '../../../services/actionsService';
import { useTheme } from '../../../hooks/useTheme';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  EmptyState,
  FiltersToggle,
  LoadingState,
  PageLayout,
} from '../../../components/ui';

const OWN_COLOR = '#2563eb';
const INDUSTRY_COLOR = '#f59e0b';
const CHINA_HONG_KONG_KEY = 'china-hong-kong';
const CHINA_HONG_KONG_LABEL = 'China + Hong Kong';

const formatPercent = (value: number) =>
  `${formatNumber(value || 0, { decimals: 1 })}%`;

type PercentDistributionRow = {
  own_pct: number;
  industry_pct: number;
};

type ViewMode = 'chart' | 'table';
type MarketDimension = 'country' | 'market';
type DistributionRow = PercentDistributionRow & {
  key: string;
  label: string;
};

const normalizeCountryLabel = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const getChinaHongKongCountryGroup = (country: string) => {
  const normalizedCountry = normalizeCountryLabel(country);

  if (
    normalizedCountry === 'china' ||
    normalizedCountry === 'mainland china' ||
    normalizedCountry === 'china mainland' ||
    normalizedCountry === "people's republic of china"
  ) {
    return 'china';
  }

  if (
    normalizedCountry === 'hong kong' ||
    normalizedCountry.includes('hong kong')
  ) {
    return 'hong-kong';
  }

  return null;
};

const mergeChinaHongKongRows = (rows: DistributionRow[]) => {
  const firstMergeIndex = rows.findIndex((row) =>
    getChinaHongKongCountryGroup(row.label)
  );

  if (firstMergeIndex < 0) return rows;

  const combined = rows.reduce<DistributionRow>(
    (total, row) => {
      if (!getChinaHongKongCountryGroup(row.label)) return total;

      return {
        ...total,
        own_pct: total.own_pct + Number(row.own_pct || 0),
        industry_pct: total.industry_pct + Number(row.industry_pct || 0),
      };
    },
    {
      key: CHINA_HONG_KONG_KEY,
      label: CHINA_HONG_KONG_LABEL,
      own_pct: 0,
      industry_pct: 0,
    }
  );

  return rows.reduce<DistributionRow[]>((mergedRows, row, index) => {
    if (!getChinaHongKongCountryGroup(row.label)) {
      mergedRows.push(row);
      return mergedRows;
    }

    if (index === firstMergeIndex) {
      mergedRows.push(combined);
    }

    return mergedRows;
  }, []);
};

const getPercentAxisMax = (rows: PercentDistributionRow[]) => {
  const maxValue = Math.max(
    0,
    ...rows.flatMap((row) => [
      Number(row.own_pct || 0),
      Number(row.industry_pct || 0),
    ])
  );

  if (maxValue <= 0) return 10;
  return Math.ceil(maxValue);
};

const getBaseOptions = (isDark: boolean): ApexOptions => ({
  chart: {
    toolbar: { show: false },
    foreColor: isDark ? '#a1a1aa' : '#71717a',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  grid: {
    borderColor: isDark ? '#27272a' : '#e4e4e7',
    strokeDashArray: 3,
  },
  legend: {
    position: 'bottom',
    horizontalAlign: 'center',
    fontSize: '13px',
  },
  dataLabels: { enabled: false },
});

const buildPercentChartOptions = (
  categories: string[],
  rows: PercentDistributionRow[],
  isDark: boolean,
  labels: {
    axis: string;
    own: string;
    industry: string;
  },
  chartType: 'line' | 'bar' = 'line'
): ApexOptions => {
  const options: ApexOptions = {
    ...getBaseOptions(isDark),
    chart: {
      ...getBaseOptions(isDark).chart,
      type: chartType,
    },
    colors: [OWN_COLOR, INDUSTRY_COLOR],
    plotOptions: {
      line: {
        isSlopeChart: false,
      },
    },
    xaxis: {
      categories,
      labels: {
        rotate: -45,
        rotateAlways: false,
        style: { fontSize: '12px' },
      },
    },
    yaxis: {
      title: { text: labels.axis },
      min: 0,
      max: getPercentAxisMax(rows),
      labels: {
        formatter: (value) => formatPercent(Number(value)),
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        {
          formatter: (value) => formatPercent(Number(value)),
          title: { formatter: () => `${labels.own}: ` },
        },
        {
          formatter: (value) => formatPercent(Number(value)),
          title: { formatter: () => `${labels.industry}: ` },
        },
      ],
    },
  };

  if (chartType === 'bar') {
    options.plotOptions = {
      ...options.plotOptions,
      line: {
        isSlopeChart: false,
      },
      bar: {
        borderRadius: 3,
        columnWidth: '46%',
      },
    };
  }

  return options;
};

const getCaliberSeries = (
  rows: ProductionCaliberCurveRow[],
  ownLabel: string,
  industryLabel: string
) => [
  {
    name: ownLabel,
    data: rows.map((row) => Number(row.own_pct || 0)),
  },
  {
    name: industryLabel,
    data: rows.map((row) => Number(row.industry_pct || 0)),
  },
];

const getWeeklySeries = (
  rows: ProductionWeeklyCurveRow[],
  ownLabel: string,
  industryLabel: string
) => [
  {
    name: ownLabel,
    data: rows.map((row) => Number(row.own_pct || 0)),
  },
  {
    name: industryLabel,
    data: rows.map((row) => Number(row.industry_pct || 0)),
  },
];

const getMarketSeries = (
  rows: PercentDistributionRow[],
  ownLabel: string,
  industryLabel: string
) => [
  {
    name: ownLabel,
    data: rows.map((row) => Number(row.own_pct || 0)),
  },
  {
    name: industryLabel,
    data: rows.map((row) => Number(row.industry_pct || 0)),
  },
];

const getVarietySeries = (
  rows: ProductionVarietyCurveRow[],
  ownLabel: string,
  industryLabel: string
) => [
  {
    name: ownLabel,
    data: rows.map((row) => Number(row.own_pct || 0)),
  },
  {
    name: industryLabel,
    data: rows.map((row) => Number(row.industry_pct || 0)),
  },
];

const getFormatUnitWeightSeries = (
  rows: ProductionFormatUnitWeightCurveRow[],
  ownLabel: string,
  industryLabel: string
) => [
  {
    name: ownLabel,
    data: rows.map((row) => Number(row.own_pct || 0)),
  },
  {
    name: industryLabel,
    data: rows.map((row) => Number(row.industry_pct || 0)),
  },
];

const getFreightTypeSeries = (
  rows: ProductionFreightTypeCurveRow[],
  ownLabel: string,
  industryLabel: string
) => [
  {
    name: ownLabel,
    data: rows.map((row) => Number(row.own_pct || 0)),
  },
  {
    name: industryLabel,
    data: rows.map((row) => Number(row.industry_pct || 0)),
  },
];

const DistributionTable = ({ rows }: { rows: DistributionRow[] }) => {
  const { t } = useTranslation('settlementsComparative');

  return (
    <Table removeWrapper aria-label={t('Tabla de distribución')}>
      <TableHeader>
        <TableColumn>{t('Grupo')}</TableColumn>
        <TableColumn align='end'>{t('Mi distribución')}</TableColumn>
        <TableColumn align='end'>{t('Industria')}</TableColumn>
        <TableColumn align='end'>{t('Diferencia')}</TableColumn>
      </TableHeader>
      <TableBody emptyContent={t('No hay datos para mostrar')}>
        {rows.map((row) => (
          <TableRow key={row.key}>
            <TableCell>{row.label}</TableCell>
            <TableCell className='text-right'>
              {formatPercent(row.own_pct)}
            </TableCell>
            <TableCell className='text-right'>
              {formatPercent(row.industry_pct)}
            </TableCell>
            <TableCell className='text-right'>
              {formatPercent(row.own_pct - row.industry_pct)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const haveSameValues = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;

  const rightValues = new Set(right);
  return left.every((value) => rightValues.has(value));
};

const SettlementsLiquidatedMix = () => {
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const { userProfile } = useUserStore();
  const { t } = useTranslation('settlementsComparative');
  const { isDark } = useTheme();
  const [data, setData] = useState<ProductionVsIndustryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedVarieties, setSelectedVarieties] = useState<string[]>([]);
  const [draftSelectedVarieties, setDraftSelectedVarieties] = useState<
    string[]
  >([]);
  const [caliberView, setCaliberView] = useState<ViewMode>('chart');
  const [varietyView, setVarietyView] = useState<ViewMode>('chart');
  const [marketView, setMarketView] = useState<ViewMode>('chart');
  const [marketDimension, setMarketDimension] =
    useState<MarketDimension>('country');
  const [mergeChinaHongKong, setMergeChinaHongKong] = useState(false);
  const [weeklyView, setWeeklyView] = useState<ViewMode>('chart');
  const [formatUnitWeightView, setFormatUnitWeightView] =
    useState<ViewMode>('chart');
  const [freightTypeView, setFreightTypeView] = useState<ViewMode>('chart');
  const caliberChartRef = useRef<HTMLDivElement>(null);
  const varietyChartRef = useRef<HTMLDivElement>(null);
  const marketChartRef = useRef<HTMLDivElement>(null);
  const weeklyChartRef = useRef<HTMLDivElement>(null);
  const formatUnitWeightChartRef = useRef<HTMLDivElement>(null);
  const freightTypeChartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logUserAction('view', 'page', 'settlements-liquidated-mix');
  }, []);

  useEffect(() => {
    setSelectedVarieties([]);
    setDraftSelectedVarieties([]);
  }, [selectedSeason, selectedSpecies?.id, userProfile?.exporter_id]);

  useEffect(() => {
    const fetchData = async () => {
      if (
        !userProfile?.exporter_id ||
        !selectedSeason ||
        !selectedSpecies?.id
      ) {
        setData(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);
      try {
        const result = await getProductionVsIndustry(
          Number(userProfile.exporter_id),
          selectedSeason,
          selectedSpecies.id,
          'etd',
          selectedVarieties
        );
        setData(result);
        if (!result) {
          setErrorMessage(
            t('No se pudo cargar la información de mix liquidado.')
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    selectedSeason,
    selectedSpecies?.id,
    selectedVarieties,
    t,
    userProfile?.exporter_id,
  ]);

  const caliberRows = data?.caliber_curve || [];
  const varietyRows = data?.variety_curve || [];
  const weeklyRows = data?.weekly_curve || [];
  const marketRows = data?.market_curve || [];
  const marketCountryRows = data?.market_country_curve || [];
  const formatUnitWeightRows = data?.format_unit_weight_curve || [];
  const freightTypeRows = data?.freight_type_curve || [];
  const ownVarieties = data?.own_varieties || [];
  const hasDraftVarietyChanges = !haveSameValues(
    draftSelectedVarieties,
    selectedVarieties
  );
  const allVarietiesSelected =
    ownVarieties.length > 0 &&
    haveSameValues(draftSelectedVarieties, ownVarieties);
  const draftChangeCount = hasDraftVarietyChanges ? 1 : 0;

  const openFilterModal = () => {
    setDraftSelectedVarieties(selectedVarieties);
    setIsFilterOpen(true);
    setTimeout(() => window.dispatchEvent(new Event('resize')), 350);
  };

  const closeFilterModal = () => {
    setIsFilterOpen(false);
  };

  const applyVarietyFilter = () => {
    const availableVarieties = new Set(ownVarieties);
    const nextSelectedVarieties = draftSelectedVarieties.filter((variety) =>
      availableVarieties.has(variety)
    );

    setSelectedVarieties(nextSelectedVarieties);
    setDraftSelectedVarieties(nextSelectedVarieties);

    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches
    ) {
      setIsFilterOpen(false);
    }
  };

  const discardDraftVarietyChanges = () => {
    setDraftSelectedVarieties(selectedVarieties);
  };
  const chartLabels = useMemo(
    () => ({
      axis: t('% distribución'),
      own: t('Mi distribución'),
      industry: t('Industria'),
    }),
    [t]
  );

  const caliberOptions = useMemo(
    () =>
      buildPercentChartOptions(
        caliberRows.map((row) => row.caliber),
        caliberRows,
        isDark,
        chartLabels
      ),
    [caliberRows, chartLabels, isDark]
  );

  const weeklyOptions = useMemo(
    () =>
      buildPercentChartOptions(
        weeklyRows.map((row) => row.label),
        weeklyRows,
        isDark,
        chartLabels
      ),
    [weeklyRows, chartLabels, isDark]
  );

  const varietyOptions = useMemo(
    () =>
      buildPercentChartOptions(
        varietyRows.map((row) => row.variety),
        varietyRows,
        isDark,
        chartLabels,
        'bar'
      ),
    [varietyRows, chartLabels, isDark]
  );

  const formatUnitWeightOptions = useMemo(
    () =>
      buildPercentChartOptions(
        formatUnitWeightRows.map((row) => row.format_unit_weight),
        formatUnitWeightRows,
        isDark,
        chartLabels,
        'bar'
      ),
    [formatUnitWeightRows, chartLabels, isDark]
  );

  const freightTypeOptions = useMemo(
    () =>
      buildPercentChartOptions(
        freightTypeRows.map((row) => row.freight_type),
        freightTypeRows,
        isDark,
        chartLabels,
        'bar'
      ),
    [freightTypeRows, chartLabels, isDark]
  );

  const hasOwnData = Boolean(data?.has_own_data);
  const hasIndustryData = Boolean(data?.has_industry_data);

  const caliberTableRows: DistributionRow[] = caliberRows.map((row) => ({
    key: row.caliber,
    label: row.caliber,
    own_pct: row.own_pct,
    industry_pct: row.industry_pct,
  }));

  const varietyTableRows: DistributionRow[] = varietyRows.map((row) => ({
    key: row.variety,
    label: row.variety,
    own_pct: row.own_pct,
    industry_pct: row.industry_pct,
  }));

  const chinaHongKongCountriesAvailable = useMemo(() => {
    const countryGroups = new Set(
      marketCountryRows
        .map((row) => getChinaHongKongCountryGroup(row.country))
        .filter(Boolean)
    );

    return countryGroups.has('china') && countryGroups.has('hong-kong');
  }, [marketCountryRows]);

  const marketCountryDistributionRows = useMemo<DistributionRow[]>(() => {
    const countryRows = marketCountryRows.map((row) => ({
      key: row.country,
      label: row.country,
      own_pct: row.own_pct,
      industry_pct: row.industry_pct,
    }));

    if (!mergeChinaHongKong || !chinaHongKongCountriesAvailable) {
      return countryRows;
    }

    return mergeChinaHongKongRows(countryRows);
  }, [chinaHongKongCountriesAvailable, marketCountryRows, mergeChinaHongKong]);

  const marketDistributionRows = useMemo<DistributionRow[]>(
    () =>
      marketDimension === 'market'
        ? marketRows.map((row) => ({
            key: row.market,
            label: row.market,
            own_pct: row.own_pct,
            industry_pct: row.industry_pct,
          }))
        : marketCountryDistributionRows,
    [marketCountryDistributionRows, marketDimension, marketRows]
  );

  const marketOptions = useMemo(
    () =>
      buildPercentChartOptions(
        marketDistributionRows.map((row) => row.label),
        marketDistributionRows,
        isDark,
        chartLabels,
        'bar'
      ),
    [marketDistributionRows, chartLabels, isDark]
  );

  const marketDownloadTitle =
    marketDimension === 'market'
      ? t('Distribución por mercado de venta')
      : t('Distribución por país de venta');
  const marketEmptyMessage =
    marketDimension === 'market'
      ? t('No hay datos por mercado de venta para mostrar.')
      : t('No hay datos por país de venta para mostrar.');

  const weeklyTableRows: DistributionRow[] = weeklyRows.map((row) => ({
    key: String(row.week),
    label: row.label,
    own_pct: row.own_pct,
    industry_pct: row.industry_pct,
  }));

  const formatUnitWeightTableRows: DistributionRow[] = formatUnitWeightRows.map(
    (row) => ({
      key: row.format_unit_weight,
      label: row.format_unit_weight,
      own_pct: row.own_pct,
      industry_pct: row.industry_pct,
    })
  );

  const freightTypeTableRows: DistributionRow[] = freightTypeRows.map(
    (row) => ({
      key: row.freight_type,
      label: row.freight_type,
      own_pct: row.own_pct,
      industry_pct: row.industry_pct,
    })
  );

  return (
    <div className='flex w-full h-screen overflow-x-hidden bg-background'>
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={closeFilterModal}
      >
        <div className='bg-background h-full flex flex-col'>
          {hasDraftVarietyChanges && (
            <Alert variant='warning' showIcon className='mx-4 mt-4'>
              <p>
                {draftChangeCount === 1
                  ? t('1 cambio sin aplicar')
                  : `${draftChangeCount} ${t('cambios sin aplicar')}`}
              </p>
              <div className='flex gap-2 mt-2.5'>
                <Button
                  variant='light'
                  size='sm'
                  onPress={discardDraftVarietyChanges}
                  className='flex-shrink-0'
                >
                  {t('Descartar')}
                </Button>
                <Button
                  color='primary'
                  size='sm'
                  className='flex-1'
                  onPress={applyVarietyFilter}
                >
                  {t('Confirmar')}
                </Button>
              </div>
            </Alert>
          )}
          <div className='flex-1 overflow-y-auto p-4 pt-6'>
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <p className='text-sm text-default-500'>{t('Filtros')}</p>
                <button
                  type='button'
                  onClick={() => setDraftSelectedVarieties([])}
                  className='text-xs text-default-500 hover:text-default-700 dark:hover:text-default-300 underline-offset-2 hover:underline'
                >
                  {t('Limpiar todo')}
                </button>
              </div>

              <SeasonFilter />

              <Select
                label={t('Variedad')}
                placeholder={t('Todas')}
                selectionMode='multiple'
                selectedKeys={new Set(draftSelectedVarieties)}
                onSelectionChange={(keys) => {
                  if (keys === 'all') {
                    setDraftSelectedVarieties(ownVarieties);
                    return;
                  }
                  setDraftSelectedVarieties(Array.from(keys).map(String));
                }}
                className='w-full'
                isDisabled={loading || ownVarieties.length === 0}
              >
                {ownVarieties.map((variety) => (
                  <SelectItem key={variety} value={variety}>
                    {variety}
                  </SelectItem>
                ))}
              </Select>

              <div className='grid grid-cols-2 gap-2'>
                <Button
                  size='sm'
                  variant='flat'
                  onPress={() =>
                    setDraftSelectedVarieties(
                      allVarietiesSelected ? [] : ownVarieties
                    )
                  }
                  isDisabled={loading || ownVarieties.length === 0}
                >
                  {allVarietiesSelected
                    ? t('Deseleccionar todos')
                    : t('Seleccionar todos')}
                </Button>
                <Button
                  size='sm'
                  color='primary'
                  onPress={applyVarietyFilter}
                  isDisabled={loading || !hasDraftVarietyChanges}
                >
                  {t('Confirmar')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ModalSlideFilter>

      <PageLayout className='flex flex-col gap-5 overflow-y-auto'>
        <div className='flex flex-col gap-3'>
          <Header
            title={t('Mix Liquidado')}
            description={t(
              'Distribución porcentual del mix liquidado vs la industria.'
            )}
          />
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={isFilterOpen ? closeFilterModal : openFilterModal}
            label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
          />
        </div>

        {errorMessage && (
          <Alert variant='danger'>{errorMessage}</Alert>
        )}

        {loading ? (
          <LoadingState label={t('Cargando mix liquidado')} />
        ) : (
          <>
            {!hasOwnData && (
              <Alert variant='warning'>
                {t(
                  'No hay liquidaciones aprobadas propias para la temporada y especie seleccionadas.'
                )}
              </Alert>
            )}

            {!hasIndustryData && hasOwnData && (
              <Alert variant='warning'>
                {t(
                  'No hay distribución de industria disponible para comparar esta selección.'
                )}
              </Alert>
            )}

            <div className='grid grid-cols-1 gap-5 xl:grid-cols-3'>
              <Card shadow='sm' className='order-1'>
                <CardHeader className='flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between'>
                  <div>
                    <h2 className='text-lg font-semibold'>{t('Calibres')}</h2>
                  </div>
                  <ChartDownloadButton
                    chartRef={caliberChartRef}
                    title={t('Distribución por calibre')}
                    subtitle={selectedSeason}
                  />
                </CardHeader>
                <Divider />
                <CardBody>
                  <Tabs
                    selectedKey={caliberView}
                    onSelectionChange={(key) => setCaliberView(key as ViewMode)}
                    size='sm'
                  >
                    <Tab key='chart' title={t('Gráfico')}>
                      <div ref={caliberChartRef}>
                        {caliberRows.length > 0 ? (
                          <ReactApexChart
                            options={caliberOptions}
                            series={
                              getCaliberSeries(
                                caliberRows,
                                chartLabels.own,
                                chartLabels.industry
                              ) as ApexAxisChartSeries
                            }
                            type='line'
                            height={360}
                          />
                        ) : (
                          <EmptyState
                            title={t(
                              'No hay datos por calibre para mostrar.'
                            )}
                          />
                        )}
                      </div>
                    </Tab>
                    <Tab key='table' title={t('Tabla')}>
                      <DistributionTable rows={caliberTableRows} />
                    </Tab>
                  </Tabs>
                </CardBody>
              </Card>

              <Card shadow='sm' className='order-2'>
                <CardHeader className='flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between'>
                  <div>
                    <h2 className='text-lg font-semibold'>{t('Variedades')}</h2>
                  </div>
                  <ChartDownloadButton
                    chartRef={varietyChartRef}
                    title={t('Distribución por variedad')}
                    subtitle={selectedSeason}
                  />
                </CardHeader>
                <Divider />
                <CardBody>
                  <Tabs
                    selectedKey={varietyView}
                    onSelectionChange={(key) => setVarietyView(key as ViewMode)}
                    size='sm'
                  >
                    <Tab key='chart' title={t('Gráfico')}>
                      <div ref={varietyChartRef}>
                        {varietyRows.length > 0 ? (
                          <ReactApexChart
                            options={varietyOptions}
                            series={
                              getVarietySeries(
                                varietyRows,
                                chartLabels.own,
                                chartLabels.industry
                              ) as ApexAxisChartSeries
                            }
                            type='bar'
                            height={360}
                          />
                        ) : (
                          <EmptyState
                            title={t(
                              'No hay datos por variedad para mostrar.'
                            )}
                          />
                        )}
                      </div>
                    </Tab>
                    <Tab key='table' title={t('Tabla')}>
                      <DistributionTable rows={varietyTableRows} />
                    </Tab>
                  </Tabs>
                </CardBody>
              </Card>

              <Card shadow='sm' className='order-5'>
                <CardHeader className='flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between'>
                  <div className='flex flex-col gap-2'>
                    <h2 className='text-lg font-semibold'>
                      {t('Mercado de Venta')}
                    </h2>
                    <Tabs
                      size='sm'
                      aria-label={t('Mercado de Venta')}
                      selectedKey={marketDimension}
                      onSelectionChange={(key) =>
                        setMarketDimension(key as MarketDimension)
                      }
                    >
                      <Tab key='country' title={t('País')} />
                      <Tab key='market' title={t('Mercado')} />
                    </Tabs>
                    {marketDimension === 'country' &&
                    chinaHongKongCountriesAvailable ? (
                      <Switch
                        size='sm'
                        isSelected={mergeChinaHongKong}
                        onValueChange={setMergeChinaHongKong}
                        classNames={{
                          label: 'text-xs text-default-500',
                        }}
                      >
                        {t('Unir China + Hong Kong')}
                      </Switch>
                    ) : null}
                  </div>
                  <ChartDownloadButton
                    chartRef={marketChartRef}
                    title={marketDownloadTitle}
                    subtitle={selectedSeason}
                  />
                </CardHeader>
                <Divider />
                <CardBody>
                  <Tabs
                    selectedKey={marketView}
                    onSelectionChange={(key) => setMarketView(key as ViewMode)}
                    size='sm'
                  >
                    <Tab key='chart' title={t('Gráfico')}>
                      <div ref={marketChartRef}>
                        {marketDistributionRows.length > 0 ? (
                          <ReactApexChart
                            options={marketOptions}
                            series={
                              getMarketSeries(
                                marketDistributionRows,
                                chartLabels.own,
                                chartLabels.industry
                              ) as ApexAxisChartSeries
                            }
                            type='bar'
                            height={360}
                          />
                        ) : (
                          <EmptyState title={marketEmptyMessage} />
                        )}
                      </div>
                    </Tab>
                    <Tab key='table' title={t('Tabla')}>
                      <DistributionTable rows={marketDistributionRows} />
                    </Tab>
                  </Tabs>
                </CardBody>
              </Card>

              <Card shadow='sm' className='order-3'>
                <CardHeader className='flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between'>
                  <div>
                    <h2 className='text-lg font-semibold'>
                      {t('Formato-peso')}
                    </h2>
                  </div>
                  <ChartDownloadButton
                    chartRef={formatUnitWeightChartRef}
                    title={t('Distribución por formato-peso')}
                    subtitle={selectedSeason}
                  />
                </CardHeader>
                <Divider />
                <CardBody>
                  <Tabs
                    selectedKey={formatUnitWeightView}
                    onSelectionChange={(key) =>
                      setFormatUnitWeightView(key as ViewMode)
                    }
                    size='sm'
                  >
                    <Tab key='chart' title={t('Gráfico')}>
                      <div ref={formatUnitWeightChartRef}>
                        {formatUnitWeightRows.length > 0 ? (
                          <ReactApexChart
                            options={formatUnitWeightOptions}
                            series={
                              getFormatUnitWeightSeries(
                                formatUnitWeightRows,
                                chartLabels.own,
                                chartLabels.industry
                              ) as ApexAxisChartSeries
                            }
                            type='bar'
                            height={360}
                          />
                        ) : (
                          <EmptyState
                            title={t(
                              'No hay datos por formato-peso para mostrar.'
                            )}
                          />
                        )}
                      </div>
                    </Tab>
                    <Tab key='table' title={t('Tabla')}>
                      <DistributionTable rows={formatUnitWeightTableRows} />
                    </Tab>
                  </Tabs>
                </CardBody>
              </Card>

              <Card shadow='sm' className='order-6'>
                <CardHeader className='flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between'>
                  <div>
                    <h2 className='text-lg font-semibold'>
                      {t('Tipo de flete')}
                    </h2>
                  </div>
                  <ChartDownloadButton
                    chartRef={freightTypeChartRef}
                    title={t('Distribución por tipo de flete')}
                    subtitle={selectedSeason}
                  />
                </CardHeader>
                <Divider />
                <CardBody>
                  <Tabs
                    selectedKey={freightTypeView}
                    onSelectionChange={(key) =>
                      setFreightTypeView(key as ViewMode)
                    }
                    size='sm'
                  >
                    <Tab key='chart' title={t('Gráfico')}>
                      <div ref={freightTypeChartRef}>
                        {freightTypeRows.length > 0 ? (
                          <ReactApexChart
                            options={freightTypeOptions}
                            series={
                              getFreightTypeSeries(
                                freightTypeRows,
                                chartLabels.own,
                                chartLabels.industry
                              ) as ApexAxisChartSeries
                            }
                            type='bar'
                            height={360}
                          />
                        ) : (
                          <EmptyState
                            title={t(
                              'No hay datos por tipo de flete para mostrar.'
                            )}
                          />
                        )}
                      </div>
                    </Tab>
                    <Tab key='table' title={t('Tabla')}>
                      <DistributionTable rows={freightTypeTableRows} />
                    </Tab>
                  </Tabs>
                </CardBody>
              </Card>

              <Card shadow='sm' className='order-4'>
                <CardHeader className='flex flex-col items-start gap-2 md:flex-row md:items-center md:justify-between'>
                  <div className='flex flex-col gap-1'>
                    <div className='flex items-center gap-2'>
                      <h2 className='text-lg font-semibold'>
                        {t('Semanas ETD')}
                      </h2>
                    </div>
                  </div>
                  <ChartDownloadButton
                    chartRef={weeklyChartRef}
                    title={t('Distribución por semana ETD')}
                    subtitle={selectedSeason}
                  />
                </CardHeader>
                <Divider />
                <CardBody>
                  <Tabs
                    selectedKey={weeklyView}
                    onSelectionChange={(key) => setWeeklyView(key as ViewMode)}
                    size='sm'
                  >
                    <Tab key='chart' title={t('Gráfico')}>
                      <div ref={weeklyChartRef}>
                        {weeklyRows.length > 0 ? (
                          <ReactApexChart
                            options={weeklyOptions}
                            series={
                              getWeeklySeries(
                                weeklyRows,
                                chartLabels.own,
                                chartLabels.industry
                              ) as ApexAxisChartSeries
                            }
                            type='line'
                            height={360}
                          />
                        ) : (
                          <EmptyState
                            title={t('No hay datos semanales para mostrar.')}
                          />
                        )}
                      </div>
                    </Tab>
                    <Tab key='table' title={t('Tabla')}>
                      <DistributionTable rows={weeklyTableRows} />
                    </Tab>
                  </Tabs>
                  {data?.missing_dates?.own ? (
                    <p className='mt-2 text-xs text-default-400'>
                      {t(
                        '{{count}} líneas propias sin ETD no se incluyen en el gráfico semanal.',
                        {
                          count: data.missing_dates.own,
                        }
                      )}
                    </p>
                  ) : null}
                </CardBody>
              </Card>
            </div>
          </>
        )}
      </PageLayout>
    </div>
  );
};

export default SettlementsLiquidatedMix;
